import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  Bell,
  BellRing,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Send,
  Mail,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";
import type { NotificationChannel, NotificationType } from "@shared/types";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import * as api from "@/lib/api";
import { queryKeys, useNotificationChannels } from "@/lib/queries";

const CHANNEL_ICONS: Record<NotificationType, LucideIcon> = {
  webhook: Webhook,
  slack: MessageSquare,
  discord: MessageCircle,
  telegram: Send,
  ntfy: BellRing,
  smtp: Mail,
};

const TYPE_LABELS: Record<NotificationType, string> = {
  webhook: "Webhook",
  slack: "Slack",
  discord: "Discord",
  telegram: "Telegram",
  ntfy: "ntfy",
  smtp: "SMTP",
};

const EMPTY_CONFIG: Record<NotificationType, Record<string, unknown>> = {
  webhook: { url: "" },
  slack: { webhookUrl: "" },
  discord: { webhookUrl: "" },
  telegram: { botToken: "", chatId: "" },
  ntfy: { serverUrl: "", topic: "" },
  smtp: { host: "", port: 587, secure: false, from: "", to: "" },
};

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

function stringValue(config: Record<string, unknown>, key: string) {
  const value = config[key];
  return typeof value === "string" ? value : "";
}

function numberValue(config: Record<string, unknown>, key: string, fallback: number) {
  const value = config[key];
  return typeof value === "number" ? value : fallback;
}

function booleanValue(config: Record<string, unknown>, key: string) {
  return config[key] === true;
}

function NotificationDialog({
  open,
  channel,
  onOpenChange,
}: {
  open: boolean;
  channel: NotificationChannel | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const initialType = channel?.type ?? "webhook";
  const [name, setName] = useState(channel?.name ?? "");
  const [type, setType] = useState<NotificationType>(initialType);
  const [config, setConfig] = useState<Record<string, unknown>>(
    channel ? { ...channel.config } : { ...EMPTY_CONFIG.webhook },
  );
  const [isDefault, setIsDefault] = useState(channel?.isDefault ?? false);
  const [headersText, setHeadersText] = useState(() => {
    const headers = channel?.type === "webhook" ? channel.config.headers : undefined;
    return headers && typeof headers === "object" ? JSON.stringify(headers, null, 2) : "";
  });
  const [headersError, setHeadersError] = useState("");
  const [busy, setBusy] = useState(false);

  function updateConfig(key: string, value: unknown) {
    setConfig((current) => ({ ...current, [key]: value }));
  }

  function selectType(nextType: NotificationType) {
    setType(nextType);
    setConfig({ ...EMPTY_CONFIG[nextType] });
    setHeadersText("");
    setHeadersError("");
  }

  function payloadConfig(): Record<string, unknown> | null {
    if (type === "slack" || type === "discord") {
      return { webhookUrl: stringValue(config, "webhookUrl") };
    }
    if (type === "telegram") {
      return { botToken: stringValue(config, "botToken"), chatId: stringValue(config, "chatId") };
    }
    if (type === "ntfy") {
      const serverUrl = stringValue(config, "serverUrl").trim();
      const token = stringValue(config, "token").trim();
      const priority = config.priority;
      return {
        topic: stringValue(config, "topic"),
        ...(serverUrl ? { serverUrl } : {}),
        ...(typeof priority === "number" ? { priority } : {}),
        ...(token ? { token } : {}),
      };
    }
    if (type === "smtp") {
      const username = stringValue(config, "username");
      const passwordValue = stringValue(config, "password");
      return {
        host: stringValue(config, "host"),
        port: numberValue(config, "port", 587),
        secure: booleanValue(config, "secure"),
        ...(username ? { username } : {}),
        ...(passwordValue ? { password: passwordValue } : {}),
        from: stringValue(config, "from"),
        to: stringValue(config, "to"),
      };
    }
    const webhookConfig: Record<string, unknown> = { url: stringValue(config, "url") };
    if (!headersText.trim()) return webhookConfig;
    try {
      const parsed: unknown = JSON.parse(headersText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setHeadersError("Headers must be a JSON object");
        return null;
      }
      for (const value of Object.values(parsed)) {
        if (typeof value !== "string") {
          setHeadersError("Every header value must be a string");
          return null;
        }
      }
      setHeadersError("");
      return { ...webhookConfig, headers: parsed };
    } catch {
      setHeadersError("Enter valid JSON headers");
      return null;
    }
  }

  async function testChannel() {
    const nextConfig = payloadConfig();
    if (!nextConfig) return;
    setBusy(true);
    try {
      await api.testNotification(type, nextConfig);
      toast.success("Test sent");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const nextConfig = payloadConfig();
    if (!nextConfig || !name.trim()) return;
    setBusy(true);
    try {
      const input = { name: name.trim(), type, config: nextConfig, isDefault };
      if (channel) await api.updateNotification(channel.id, input);
      else await api.createNotification(input);
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      toast.success(channel ? "Channel updated" : "Channel created");
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{channel ? "Edit notification channel" : "Add notification channel"}</DialogTitle>
          <DialogDescription>Configure where Vigil sends monitor alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="notification-name">Name</Label>
            <Input id="notification-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Operations alerts" />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(value) => selectType(value as NotificationType)} disabled={Boolean(channel)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(TYPE_LABELS) as NotificationType[]).map((value) => (
                  <SelectItem key={value} value={value}>{TYPE_LABELS[value]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {type === "webhook" && (
            <>
              <Field label="URL"><Input type="url" value={stringValue(config, "url")} onChange={(event) => updateConfig("url", event.target.value)} placeholder="https://example.com/hooks/vigil" /></Field>
              <Field label="Headers (optional JSON)">
                <Textarea value={headersText} onChange={(event) => setHeadersText(event.target.value)} placeholder={'{"Authorization": "Bearer …"}'} className="min-h-24 font-mono text-xs" />
                {headersError && <p className="text-xs text-destructive">{headersError}</p>}
              </Field>
            </>
          )}
          {(type === "slack" || type === "discord") && (
            <Field label="Webhook URL"><Input type="url" value={stringValue(config, "webhookUrl")} onChange={(event) => updateConfig("webhookUrl", event.target.value)} /></Field>
          )}
          {type === "telegram" && (
            <>
              <Field label="Bot token"><Input type="password" value={stringValue(config, "botToken")} onChange={(event) => updateConfig("botToken", event.target.value)} /></Field>
              <Field label="Chat ID"><Input value={stringValue(config, "chatId")} onChange={(event) => updateConfig("chatId", event.target.value)} /></Field>
            </>
          )}
          {type === "ntfy" && (
            <>
              <Field label="Server URL"><Input type="url" value={stringValue(config, "serverUrl")} onChange={(event) => updateConfig("serverUrl", event.target.value)} placeholder="https://ntfy.sh" /></Field>
              <Field label="Topic"><Input value={stringValue(config, "topic")} onChange={(event) => updateConfig("topic", event.target.value)} /></Field>
              <Field label="Priority (optional)"><Input type="number" min={1} max={5} value={stringValue(config, "priority") || numberValue(config, "priority", 0) || ""} onChange={(event) => updateConfig("priority", event.target.value ? Number(event.target.value) : undefined)} /></Field>
              <Field label="Token (optional)"><Input type="password" value={stringValue(config, "token")} onChange={(event) => updateConfig("token", event.target.value)} /></Field>
            </>
          )}
          {type === "smtp" && (
            <>
              <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                <Field label="Host"><Input value={stringValue(config, "host")} onChange={(event) => updateConfig("host", event.target.value)} /></Field>
                <Field label="Port"><Input type="number" min={1} max={65535} value={numberValue(config, "port", 587)} onChange={(event) => updateConfig("port", Number(event.target.value))} /></Field>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label htmlFor="smtp-secure">Secure connection</Label>
                <Switch id="smtp-secure" checked={booleanValue(config, "secure")} onCheckedChange={(checked) => updateConfig("secure", checked)} />
              </div>
              <Field label="Username (optional)"><Input value={stringValue(config, "username")} onChange={(event) => updateConfig("username", event.target.value)} /></Field>
              <Field label="Password (optional)"><Input type="password" value={stringValue(config, "password")} onChange={(event) => updateConfig("password", event.target.value)} /></Field>
              <Field label="From"><Input type="email" value={stringValue(config, "from")} onChange={(event) => updateConfig("from", event.target.value)} /></Field>
              <Field label="To"><Input type="email" value={stringValue(config, "to")} onChange={(event) => updateConfig("to", event.target.value)} /></Field>
            </>
          )}
          <label className="flex items-center gap-3 rounded-lg border p-3 text-sm">
            <Checkbox checked={isDefault} onCheckedChange={(checked) => setIsDefault(checked === true)} />
            Use as default for new monitors
          </label>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="outline" onClick={() => void testChannel()} disabled={busy}>Test</Button>
          <Button type="button" onClick={() => void save()} disabled={busy || !name.trim()}>{busy ? "Working…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

export default function NotificationsPage() {
  const { data: channels, isLoading } = useNotificationChannels();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ key: number; channel: NotificationChannel | null } | null>(null);
  const [deleting, setDeleting] = useState<NotificationChannel | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  function openDialog(channel: NotificationChannel | null) {
    setDialog({ key: Date.now(), channel });
  }

  async function testSaved(channel: NotificationChannel) {
    setBusyId(channel.id);
    try {
      await api.testNotification(channel.type, channel.config);
      toast.success("Test sent");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  async function removeChannel() {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      await api.deleteNotification(deleting.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      toast.success("Channel deleted");
      setDeleting(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader
        title="Notifications"
        description="Alert channels for monitor up/down events"
        actions={<Button onClick={() => openDialog(null)}><Plus />Add channel</Button>}
      />
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">{[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-36 rounded-xl" />)}</div>
      ) : !channels?.length ? (
        <EmptyState icon={Bell} title="No notification channels" description="Add a channel to receive monitor up and down alerts." action={<Button onClick={() => openDialog(null)}><Plus />Add channel</Button>} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {channels.map((channel) => {
            const Icon = CHANNEL_ICONS[channel.type];
            return (
              <Card key={channel.id}>
                <CardHeader className="flex-row items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted"><Icon className="size-5 text-muted-foreground" /></div>
                    <div className="min-w-0">
                      <CardTitle className="truncate text-base">{channel.name}</CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">{TYPE_LABELS[channel.type]}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label={`Actions for ${channel.name}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openDialog(channel)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => void testSaved(channel)} disabled={busyId === channel.id}>Test</DropdownMenuItem>
                      <DropdownMenuItem variant="destructive" onClick={() => setDeleting(channel)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>{channel.isDefault && <Badge variant="secondary">Default</Badge>}</CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {dialog && <NotificationDialog key={dialog.key} open channel={dialog.channel} onOpenChange={(open) => { if (!open) setDialog(null); }} />}
      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete notification channel?</AlertDialogTitle><AlertDialogDescription>Monitors using this channel simply stop alerting through it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void removeChannel()} disabled={busyId !== null}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
